import type { QueryClient } from '@tanstack/react-query';
import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetSummary,
    SchemaTemplateResolver_GetMailTemplates,
    SchemaTemplateResolver_GetOfferTemplates,
} from 'cpool-tables/backend-resolvers';
import { toast } from 'react-toastify';

import { prepareFreetextValuesForSubmit } from './prepareFreetextValuesForSubmit';
import type { EmailComposerForm } from '../schemas/emailComposerForm.schema';
import { APICommunicator } from '~/api/APICommunicator';

type Props = {
    lead: SchemaLoanRequestResolver_GetSummary[number] | SchemaLoanRequestResolver_Get;
    template?: SchemaTemplateResolver_GetMailTemplates[number] | SchemaTemplateResolver_GetOfferTemplates[number];
    successMessage: string;
    queryClient: QueryClient;
};

export const emailSubmit =
    ({ lead, queryClient, successMessage, template }: Props) =>
    async (values: EmailComposerForm) => {
        const [freeTextsBody, freeTextsOffers] = prepareFreetextValuesForSubmit(values);

        const mailContent = template?.data.mailContent;
        const mailHTMLContent = template?.data.mailHTMLContent;

        const formData = new FormData();
        formData.append('subject', template?.data.subject ?? '');
        formData.append('technicalDescription', template?.technicalDescription ?? '');
        formData.append('aggregateVersion', template?.aggregateVersion?.toString() ?? '');
        formData.append('textBody', mailContent ?? '');
        formData.append('htmlBody', mailHTMLContent ?? '');
        if (freeTextsBody.length > 0) formData.append('freeTextsBody', JSON.stringify(freeTextsBody));
        if (freeTextsOffers.length > 0) formData.append('freeTextsOffers', JSON.stringify(freeTextsOffers));

        if (template && 'data' in template && 'offerItemContent' in template.data) {
            formData.append('offerItemContent', template.data.offerItemContent ?? '');
        }
        if (template && 'data' in template && 'offerItemHTMLContent' in template.data) {
            formData.append('offerItemHTMLContent', template.data.offerItemHTMLContent ?? '');
        }

        if (values.offers && values.offers.length > 0) {
            formData.append('offers', JSON.stringify(values.offers));
        }
        formData.append('recipient', values.recipient);
        const response = await APICommunicator.apiFetch({
            context: {
                body: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                method: 'POST',
            },
            route: `/messages/send/email?aggregateId=${lead.id}`,
        });

        queryClient.invalidateQueries({ queryKey: ['history-events', lead.id, 'communication'] });
        queryClient.invalidateQueries({ queryKey: ['history-events', lead.id, 'all'] });
        queryClient.invalidateQueries({ queryKey: ['history-events', lead.id, 'events'] });

        if (response instanceof Response && response.ok) {
            toast.success(successMessage);
        }
        return response;
    };
